'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { useSound } from '@/hooks/useSound';

type LoginFormData = {
  username: string;
  password: string;
  remember: boolean;
};

const ADMIN_USERNAME = 'admin';
const ADMIN_PASSWORD = 'Indramayu24#';

export default function AuthForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const { play } = useSound();
  const router = useRouter();

  const loginForm = useForm<LoginFormData>({ defaultValues: { remember: false } });

  const handleLogin = async (data: LoginFormData) => {
    play('click');
    setIsLoading(true);
    setLoginError('');

    // Simulate brief loading
    await new Promise((resolve) => setTimeout(resolve, 500));

    if (
      data.username.toLowerCase() === ADMIN_USERNAME &&
      data.password === ADMIN_PASSWORD
    ) {
      // Set cookie for middleware auth check
      const maxAge = data.remember ? 60 * 60 * 24 * 30 : 60 * 60 * 8; // 30 days or 8 hours
      document.cookie = `admin_logged_in=true; path=/; max-age=${maxAge}; SameSite=Strict`;
      play('success');
      router.push('/dashboard');
    } else {
      setLoginError('Username atau password salah. Silakan coba lagi.');
      play('click');
    }

    setIsLoading(false);
  };

  return (
    <div
      className="w-full max-w-sm animate-fade-in-up"
      style={{ animationDelay: '0.1s' }}
    >
      <div className="glass-card-cyan p-8">
        {/* Logo mobile */}
        <div className="flex items-center gap-2 mb-8 lg:hidden">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #00F0FF, #A855F7)' }}
          >
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-background">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <span className="font-bold neon-text-cyan">Rental Giaz</span>
        </div>

        <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-5">
          <h2 className="text-xl font-bold text-foreground">Selamat Datang Kembali</h2>
          <p className="text-sm text-muted-foreground">Masuk ke panel admin Anda</p>

          {loginError && (
            <div
              className="px-4 py-3 rounded-lg text-sm"
              style={{
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                color: '#EF4444',
              }}
            >
              {loginError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Username
            </label>
            <input
              type="text"
              className="input-neon w-full px-4 py-2.5 text-sm"
              placeholder="admin"
              autoComplete="username"
              {...loginForm.register('username', {
                required: 'Username wajib diisi',
              })}
            />
            {loginForm.formState.errors.username && (
              <p className="mt-1 text-xs text-status-danger">{loginForm.formState.errors.username.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                className="input-neon w-full px-4 py-2.5 pr-10 text-sm"
                placeholder="••••••••"
                autoComplete="current-password"
                {...loginForm.register('password', { required: 'Password wajib diisi' })}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {loginForm.formState.errors.password && (
              <p className="mt-1 text-xs text-status-danger">{loginForm.formState.errors.password.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 rounded"
                style={{ accentColor: 'var(--primary)' }}
                {...loginForm.register('remember')}
              />
              <span className="text-sm text-muted-foreground">Ingat saya</span>
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn-solid-cyan py-2.5 rounded-lg text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2"
            style={{ minHeight: '42px' }}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin" width="16" height="16" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Memverifikasi...
              </>
            ) : (
              'Masuk ke Dashboard'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}